import type { FC, ChangeEvent, FocusEvent } from 'react';
import { useState, useRef, useEffect } from 'react';
import { FormGroup } from '@patternfly/react-core/dist/dynamic/components/Form';
import { FormHelperText } from '@patternfly/react-core/dist/dynamic/components/Form';
import { HelperText } from '@patternfly/react-core/dist/dynamic/components/HelperText';
import { HelperTextItem } from '@patternfly/react-core/dist/dynamic/components/HelperText';
import { TextInputTypes } from '@patternfly/react-core/dist/dynamic/components/TextInput';
import { ValidatedOptions } from '@patternfly/react-core/dist/dynamic/helpers/constants';
import type { FormikValues } from 'formik';
import { useFormikContext, useField } from 'formik';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { InputField, getFieldId } from '@console/shared';
import { useFormikValidationFix } from '@console/shared/src/hooks/useFormikValidationFix';
import { CREATE_APPLICATION_KEY, UNASSIGNED_KEY } from '../../const';
import { sanitizeApplicationValue } from '../../utils/application-utils';
import ApplicationDropdown from './ApplicationDropdown';

interface ApplicationSelectorProps {
  namespace?: string;
  noProjectsAvailable?: boolean;
  subPath?: string;
}

const ApplicationSelector: FC<ApplicationSelectorProps> = ({
  namespace,
  noProjectsAvailable,
  subPath,
}) => {
  const { t } = useTranslation();
  const [applicationsAvailable, setApplicationsAvailable] = useState(true);
  // Initialize as undefined to detect the first load (even if empty)
  const availableApplications = useRef<string[] | undefined>();
  const projectsAvailable = !noProjectsAvailable;

  const [selectedKey, { touched, error }] = useField(
    subPath ? `${subPath}.application.selectedKey` : 'application.selectedKey',
  );
  const [nameField] = useField(subPath ? `${subPath}.application.name` : 'application.name');
  const { setFieldValue, setFieldTouched } = useFormikContext<FormikValues>();
  const [applicationExists, setApplicationExists] = useState<boolean>(false);
  const applicationNameInputRef = useRef<HTMLInputElement>();
  const fieldId = getFieldId('application-name', 'dropdown');
  const isValid = !(touched && error);
  const errorMessage = !isValid ? error : '';

  useFormikValidationFix(selectedKey.value);

  const onDropdownChange = (key: string, application: string) => {
    setFieldValue(selectedKey.name, key);
    setFieldTouched(selectedKey.name, true);
    setFieldValue(nameField.name, sanitizeApplicationValue(application, key));
    setFieldTouched(nameField.name, true);
    setApplicationExists(false);
  };

  const handleOnLoad = (applicationList: { [key: string]: string }) => {
    const noApplicationsAvailable = _.isEmpty(applicationList);
    const newKeys = _.keys(applicationList);
    // Only update state if the available applications actually changed
    if (!_.isEqual(newKeys, availableApplications.current)) {
      availableApplications.current = newKeys;
      setApplicationsAvailable(!noApplicationsAvailable);
      if (noApplicationsAvailable) {
        setFieldValue(selectedKey.name, '');
        setFieldValue(
          nameField.name,
          (selectedKey.value !== UNASSIGNED_KEY && nameField.value) ?? '',
        );
      }
    }
  };

  const actionItems = [
    {
      actionTitle: t('topology~Create application'),
      actionKey: CREATE_APPLICATION_KEY,
    },
    {
      actionTitle: t('topology~No application group'),
      actionKey: UNASSIGNED_KEY,
    },
  ];

  const handleAppChange = (event: ChangeEvent<HTMLInputElement>) => {
    setApplicationExists(
      availableApplications.current?.includes(event.target.value.trim()) ?? false,
    );
  };

  const handleAppBlur = (event: FocusEvent<HTMLInputElement>) => {
    const trimmedApplicationName = event.target.value.trim();
    setFieldValue(nameField.name, trimmedApplicationName);
  };

  const label = t('topology~Application');
  const inputHelpText = applicationExists
    ? t('topology~Warning: the application grouping already exists.')
    : t('topology~A unique name given to the application grouping to label your resources.');

  useEffect(() => {
    if (selectedKey.value === CREATE_APPLICATION_KEY) {
      applicationNameInputRef.current?.focus();
    }
  }, [selectedKey.value]);

  return (
    <>
      {projectsAvailable && applicationsAvailable && (
        <FormGroup fieldId={fieldId} label={label}>
          <ApplicationDropdown
            id={fieldId}
            ariaLabel={label}
            isFullWidth
            menuClassName="dropdown-menu--text-wrap"
            namespace={namespace}
            actionItems={actionItems}
            autoSelect
            selectedKey={selectedKey.value}
            onChange={onDropdownChange}
            onLoad={handleOnLoad}
          />

          <FormHelperText>
            <HelperText>
              {!isValid ? (
                <HelperTextItem variant="error">{errorMessage}</HelperTextItem>
              ) : (
                <HelperTextItem>
                  {t('topology~Select an Application to group this component.')}
                </HelperTextItem>
              )}
            </HelperText>
          </FormHelperText>
        </FormGroup>
      )}
      {(!applicationsAvailable || selectedKey.value === CREATE_APPLICATION_KEY) && (
        <InputField
          type={TextInputTypes.text}
          required={selectedKey.value === CREATE_APPLICATION_KEY}
          name={nameField.name}
          ref={applicationNameInputRef}
          label={t('topology~Application name')}
          data-test-id="application-form-app-input"
          helpText={inputHelpText}
          validated={applicationExists ? ValidatedOptions.warning : ValidatedOptions.default}
          onChange={handleAppChange}
          onBlur={handleAppBlur}
        />
      )}
    </>
  );
};

export default ApplicationSelector;
